import { Link } from "react-router-dom";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHelmet from "@/components/SEOHelmet";
import StructuredData from "@/components/StructuredData";
import { FileText, Shield, Building2, MapPin, BookOpen, Phone, Info } from "lucide-react";

const Sitemap = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Praxis Versichert",
    url: "https://praxis-versichert.de",
    logo: "https://praxis-versichert.de/logo.png",
    description: "Spezialisierte Cyberversicherung für Arztpraxen in Deutschland"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://praxis-versichert.de"
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Sitemap"
      }
    ]
  };

  const sitemapSections = [
    {
      title: "Hauptseiten",
      icon: FileText,
      links: [
        { to: "/", label: "Startseite" },
        { to: "/cyberrisiken", label: "Cyberrisiken" },
        { to: "/faq", label: "Häufige Fragen" },
        { to: "/ueber-uns", label: "Über uns" },
        { to: "/termin-buchen", label: "Termin buchen" },
        { to: "/newsletter", label: "Newsletter" }
      ]
    },
    {
      title: "Tarifwissen",
      icon: Shield,
      links: [
        { to: "/tarifwissen", label: "Tarifwissen Übersicht" },
        { to: "/tarifwissen/allianz", label: "Allianz Cyberversicherung" },
        { to: "/tarifwissen/hiscox", label: "Hiscox Cyberversicherung" },
        { to: "/tarifwissen/hdi", label: "HDI Cyberversicherung" },
        { to: "/tarifwissen/markel", label: "Markel Cyberversicherung" },
        { to: "/tarifwissen/vhv", label: "VHV Cyberversicherung" },
        { to: "/tarifwissen/axa", label: "AXA Cyberversicherung" },
        { to: "/tarifwissen/alte-leipziger", label: "Alte Leipziger Cyberversicherung" },
        { to: "/tarifwissen/ruv", label: "R+V Cyberversicherung" },
        { to: "/tarifwissen/stoik", label: "STOIK Cyberversicherung" },
        { to: "/tarifwissen/nuernberger", label: "Nürnberger Cyberversicherung" },
        { to: "/tarifwissen/cogitanda", label: "Cogitanda Cyberversicherung" },
        { to: "/tarifwissen/ergo", label: "ERGO Cyberversicherung" },
        { to: "/tarifwissen/zurich", label: "Zurich Cyberversicherung" },
        { to: "/tarifwissen/gothaer", label: "Gothaer Cyberversicherung" },
        { to: "/tarifwissen/signal-iduna", label: "Signal Iduna Cyberversicherung" },
        { to: "/tarifwissen/wuerttembergische", label: "Württembergische Cyberversicherung" }
      ]
    },
    {
      title: "Weitere Versicherungen",
      icon: Building2,
      links: [
        { to: "/weitere-versicherungen", label: "Versicherungen Übersicht" },
        { to: "/weitere-versicherungen/berufshaftpflicht", label: "Berufshaftpflicht" },
        { to: "/weitere-versicherungen/betriebshaftpflicht", label: "Betriebshaftpflicht" },
        { to: "/weitere-versicherungen/inhaltsversicherung", label: "Inhaltsversicherung" },
        { to: "/weitere-versicherungen/praxisausfall", label: "Praxisausfallversicherung" },
        { to: "/weitere-versicherungen/rechtsschutz", label: "Rechtsschutz" },
        { to: "/weitere-versicherungen/elektronik", label: "Elektronikversicherung" }
      ]
    },
    {
      title: "Regionale Cyberversicherung",
      icon: MapPin,
      links: [
        { to: "/cyberversicherung", label: "Cyberversicherung Hub" },
        { to: "/cyberversicherung/vergleichsrechner", label: "Vergleichsrechner" },
        { to: "/cyberversicherung/muenchen", label: "München" },
        { to: "/cyberversicherung/berlin", label: "Berlin" },
        { to: "/cyberversicherung/hamburg", label: "Hamburg" },
        { to: "/cyberversicherung/koeln", label: "Köln" },
        { to: "/cyberversicherung/frankfurt", label: "Frankfurt" },
        { to: "/cyberversicherung/stuttgart", label: "Stuttgart" },
        { to: "/cyberversicherung/duesseldorf", label: "Düsseldorf" },
        { to: "/cyberversicherung/dortmund", label: "Dortmund" },
        { to: "/cyberversicherung/essen", label: "Essen" },
        { to: "/cyberversicherung/leipzig", label: "Leipzig" }
      ]
    },
    {
      title: "Blog & Ratgeber",
      icon: BookOpen,
      links: [
        { to: "/blog", label: "Blog Übersicht" },
        { to: "/blog/kategorien", label: "Blog Kategorien" }
      ]
    },
    {
      title: "Rechtliches & Kontakt",
      icon: Info,
      links: [
        { to: "/impressum", label: "Impressum" },
        { to: "/datenschutz", label: "Datenschutz" },
        { to: "/erstinformation", label: "Erstinformation" }
      ]
    }
  ];

  return (
    <>
      <SEOHelmet
        title="Sitemap | Praxis Versichert - Alle Seiten im Überblick"
        description="Vollständige Übersicht aller Seiten von Praxis Versichert: Cyberversicherungen, regionale Angebote, Tarifvergleiche und Ratgeber für Arztpraxen."
        canonical="https://praxis-versichert.de/sitemap"
      />
      <StructuredData data={[organizationSchema, breadcrumbSchema]} />
      
      <div className="min-h-screen relative">
        <Navigation />
        
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 relative z-10">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-12 sm:mb-16">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary via-primary-glow to-accent bg-clip-text text-transparent">
                Sitemap
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Alle wichtigen Seiten von Praxis Versichert auf einen Blick
              </p>
            </header>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
              {sitemapSections.map((section, idx) => {
                const Icon = section.icon;
                return (
                  <section key={idx} className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h2 className="text-xl font-semibold text-foreground">
                        {section.title}
                      </h2>
                    </div>
                    <ul className="space-y-2">
                      {section.links.map((link, linkIdx) => (
                        <li key={linkIdx}>
                          <Link
                            to={link.to}
                            className="text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/40 group-hover:bg-primary transition-colors" />
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <p className="text-sm text-muted-foreground">
                Für die maschinelle Verarbeitung steht unsere{" "}
                <a 
                  href="/sitemap.xml" 
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  XML-Sitemap
                </a>{" "}
                zur Verfügung.
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Sitemap;